import { Accordion, useAccordionItemContext } from '@chakra-ui/react';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  IQuickBook,
  IQuickBookRefundSyncSchema,
  QuickBookRefundSyncSchema,
} from '@monetize/types/app';
import { useEffect } from 'react';
import {
  Control,
  Controller,
  UseFormHandleSubmit,
  useForm,
} from 'react-hook-form';
import { handleApiErrorToast } from '../../../../../api/axios';
import { useUpdateLineItem } from '../../../../../api/refundService';
import {
  MAccordionCustomButton,
  MBox,
  MButton,
  MDivider,
  MFlex,
  MFormField,
  MInput,
  MText,
} from '../../../../../components/Monetize';
import { logger } from '../../../../../services/logger';

interface QuickBooksRefundSyncBodyProps {
  control: Control<IQuickBookRefundSyncSchema>;
  isLoading: boolean;
  isDirty: boolean;
  handleSubmit: UseFormHandleSubmit<IQuickBookRefundSyncSchema>;
  onSubmitHandler: (data: IQuickBookRefundSyncSchema) => Promise<void>;
}

// v3 dropped v2's render-prop children (`{({isExpanded}) => ...}`) on
// AccordionItem - the isExpanded-dependent content moves into its own child
// component reading `useAccordionItemContext()`, same pattern as Track H's
// other shared wrappers.
const QuickBooksRefundSyncBody = ({
  control,
  isLoading,
  isDirty,
  handleSubmit,
  onSubmitHandler,
}: QuickBooksRefundSyncBodyProps) => {
  const { expanded: isExpanded } = useAccordionItemContext();

  return (
    <>
      <MAccordionCustomButton
        isExpanded={isExpanded}
        label="Sync Refunds to Quickbooks"
      ></MAccordionCustomButton>
      <Accordion.ItemContent>
        <Accordion.ItemBody>
          <MText mb={4}>
            To sync refunds from MonetizeNow to Quickbooks, create a line-item
            for refunds in Quickbooks and provide that Product ID here.
          </MText>

          <MFormField label="Refund Placeholder Product ID">
            <Controller
              name="productId"
              control={control}
              render={({ field }) => (
                <MInput
                  {...field}
                  isDisabled={isLoading}
                  placeholder="Product ID"
                ></MInput>
              )}
            />
          </MFormField>

          <MDivider my={4}></MDivider>
          <MFlex justifyContent="end">
            <MButton
              isLoading={isLoading}
              isDisabled={!isDirty}
              onClick={handleSubmit(onSubmitHandler)}
            >
              Save
            </MButton>
          </MFlex>
        </Accordion.ItemBody>
      </Accordion.ItemContent>
    </>
  );
};

export const QuickBooksRefundSync = ({
  connection,
}: {
  connection: IQuickBook;
}) => {
  const {
    handleSubmit,
    control,
    reset,
    formState: { isDirty },
  } = useForm<IQuickBookRefundSyncSchema>({
    resolver: zodResolver(QuickBookRefundSyncSchema),
    mode: 'onChange',
    defaultValues: {
      productId: connection?.refundLineItemId ?? '',
    },
  });

  useEffect(() => {
    reset({
      productId: connection?.refundLineItemId ?? '',
    });
  }, [reset, connection?.refundLineItemId]);

  const { mutateAsync: doUpdateLineItem, isPending: isLoading } =
    useUpdateLineItem({
      onError: (err) => {
        handleApiErrorToast(err);
      },
    });

  const onSubmitHandler = async (data: IQuickBookRefundSyncSchema) => {
    try {
      await doUpdateLineItem({
        refundLineItemId: data.productId,
      });
    } catch (error) {
      logger.warn('Line update error', error);
    }
  };
  return (
    <MBox mb={6}>
      <Accordion.Root multiple w="100%">
        <Accordion.Item
          mt={4}
          borderWidth="1px"
          borderRadius="md"
          borderColor="tGray.back"
          value="item-0"
        >
          <QuickBooksRefundSyncBody
            control={control}
            isLoading={isLoading}
            isDirty={isDirty}
            handleSubmit={handleSubmit}
            onSubmitHandler={onSubmitHandler}
          />
        </Accordion.Item>
      </Accordion.Root>
    </MBox>
  );
};
